import { useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import { useTracker } from '@archipro-website/tracker';

import {
    BOOKING_AVAILABILITY_API_ROUTE,
    type BookingAvailabilityApiResponse,
} from '~/routes/remix-api.booking.availability';
import {
    BOOKING_REQUEST_AVAILABILITY_API_ROUTE,
    type BookingRequestAvailabilityApiResponse,
} from '~/routes/remix-api.booking.request-availability';
import type { CalendarAvailabilityData } from '../api/get-calendar-availability.server';
import type {
    BookingRosterMember,
    RequestAvailabilityConfigSummary,
} from '../api/get-request-availability.server';
import { useBookingSchedule } from '../hooks/use-booking-schedule';
import { toCompanyTierAvailability } from '../utils/build-request-availability';
import type { BookMeetingGateIntent } from '../book-meeting-gate-state';
import {
    readMeetingDetails,
    resolveMeetingTarget,
    type BookingSlot,
    type MeetingDetails,
} from '../domain/book-meeting-flow';
import { BookMeetingGateSteps } from './BookMeetingGateSteps';
import {
    BookMeetingGateUnavailable,
    type BookMeetingUnavailableCode,
} from './BookMeetingGateUnavailable';

/** Stable empty list, so the seeding effect is not re-run by a new array each render. */
const EMPTY_ROSTER: BookingRosterMember[] = [];

const ENQUIRIES_CLOSED_MESSAGE =
    'This team is not taking meeting requests online. Send them an enquiry instead.';

function getBrowserTimezone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return 'Pacific/Auckland';
    }
}

export interface BookMeetingSubject {
    subjectName: string;
    professionalName: string;
    /** The person's own photo, when the professional has one public. */
    image?: string;
    companyLogo?: string | null;
    logoBackgroundColor?: string | null;
    /** No public team member, so the subject is the practice itself. */
    isCompany: boolean;
    /** A connected calendar, so the time is booked rather than requested. */
    isBooked: boolean;
}

/**
 * Availability handed in rather than fetched, for the Business Manager
 * preview: the real gate over made-up days, with nothing on the wire.
 */
export interface BookMeetingPreviewData {
    roster: BookingRosterMember[];
    /** The working week offered when the time is requested. */
    requestAvailability: CalendarAvailabilityData;
    requestConfig?: RequestAvailabilityConfigSummary;
    /** Offered for whichever session the target lands on. */
    sessionAvailability?: CalendarAvailabilityData;
}

interface BookMeetingGateBodyProps {
    intent: BookMeetingGateIntent;
    /**
     * The session the form's routing lands on: the client's prediction before
     * the enquiry is sent, the server's answer after. It decides the calendar
     * whoever was picked, so the picker steps aside.
     */
    routedSessionId?: number | null;
    /** The person's own form was answered, so there is nobody else to choose. */
    lockedToPreferred?: boolean;
    /** Shown over the calendar, e.g. why the reader is choosing again. */
    notice?: string | null;
    /** Reports who the reader is meeting, for the modal's left pane. */
    onSubject?: (subject: BookMeetingSubject) => void;
    /** Reports the booking link's details (name, place, cover) once known. */
    onDetails?: (details: MeetingDetails | null) => void;
    /** The reader confirmed a time. */
    onSlotChosen: (slot: BookingSlot) => void;
    /** Back from the first step, to the form's questions. */
    onBack?: () => void;
    /** Hands the reader to the enquiry gate when booking is not possible. */
    onEnquireInstead?: () => void;
    preview?: BookMeetingPreviewData;
}

/**
 * Availability and the choice of person, day and time for the booking gate.
 *
 * Split from the modal shell so the shell only decides which step is on
 * screen, and from the step components so they only draw.
 */
export const BookMeetingGateBody = ({
    intent,
    routedSessionId = null,
    lockedToPreferred = false,
    notice,
    onSubject,
    onDetails,
    onSlotChosen,
    onBack,
    onEnquireInstead,
    preview,
}: BookMeetingGateBodyProps) => {
    const serverTimezone = getBrowserTimezone();
    const assignedSessionId = intent.sessionIds?.[0] ?? null;
    /** The booking link whose hours a request is offered over. */
    const configSessionId = routedSessionId ?? assignedSessionId;

    // The roster and the working week in one quick call: no calendar provider
    // is touched, so this returns long before real availability would.
    const rosterFetcher = useFetcher<BookingRequestAvailabilityApiResponse>();
    const rosterUrl = useMemo(() => {
        const params = new URLSearchParams({
            professional: intent.professionalUrlSegment,
        });
        if (configSessionId) params.set('sessionId', String(configSessionId));
        return `${BOOKING_REQUEST_AVAILABILITY_API_ROUTE}?${params.toString()}`;
    }, [intent.professionalUrlSegment, configSessionId]);
    const rosterLoadedRef = useRef<string | null>(null);
    useEffect(() => {
        if (preview || rosterLoadedRef.current === rosterUrl) return;
        rosterLoadedRef.current = rosterUrl;
        rosterFetcher.load(rosterUrl);
        // rosterFetcher is deliberately omitted: its own state changes must not
        // re-trigger the load and cancel the request in flight.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rosterUrl, preview]);

    // Memoised so a preview's answer is as stable as a fetcher's: the details
    // reported up from it re-render the host, which re-renders this.
    const previewRosterData = useMemo<
        BookingRequestAvailabilityApiResponse | undefined
    >(
        () =>
            preview
                ? {
                      roster: preview.roster,
                      availability: preview.requestAvailability,
                      requestConfig: preview.requestConfig,
                      allowsEnquiries: true,
                  }
                : undefined,
        [preview],
    );
    const rosterData = previewRosterData ?? rosterFetcher.data;

    /**
     * Kept from the last load that carried one, because the fetcher empties
     * while it revalidates, and the roster blinking out took the chosen person
     * with it.
     */
    const [lastRoster, setLastRoster] =
        useState<BookingRosterMember[]>(EMPTY_ROSTER);
    const fetchedRoster = rosterData?.roster;
    useEffect(() => {
        if (fetchedRoster?.length) setLastRoster(fetchedRoster);
    }, [fetchedRoster]);
    const roster = fetchedRoster?.length ? fetchedRoster : lastRoster;
    const requestAvailability = rosterData?.availability;
    /**
     * A request needs an enquiry to land in. Absent until the roster loads,
     * and absent from the `enquiriesClosed` refusal, which the server sends in
     * place of a roster when nobody is left to offer — that refusal is the
     * answer "no".
     */
    const allowsEnquiries =
        rosterData?.error?.code === 'enquiriesClosed'
            ? false
            : (rosterData?.allowsEnquiries ?? true);

    /**
     * The CTA named someone the roster does not have — a stale id, or a member
     * unpublished since the page rendered.
     */
    const preferredIsUnknown =
        Boolean(intent.preferredTeamMemberId) &&
        roster.length > 0 &&
        !roster.some(
            (member) => member.teamMemberId === intent.preferredTeamMemberId,
        );

    /**
     * Who the gate opens on: the person the CTA named, and nobody at all when
     * it named none — a CTA that named no one is asking the practice.
     */
    const defaultTeamMemberId = useMemo(() => {
        if (!intent.preferredTeamMemberId || preferredIsUnknown) return null;
        return (
            roster.find(
                (member) =>
                    member.teamMemberId === intent.preferredTeamMemberId,
            )?.teamMemberId ?? null
        );
    }, [roster, intent.preferredTeamMemberId, preferredIsUnknown]);

    /** The reader's own pick, which stands once made. */
    const [chosenTeamMemberId, setChosenTeamMemberId] = useState<number | null>(
        null,
    );
    const hasChosenRef = useRef(false);
    const setSelectedTeamMemberId = (next: number | null) => {
        hasChosenRef.current = true;
        setChosenTeamMemberId(next);
    };
    const selectedTeamMemberId =
        hasChosenRef.current && !lockedToPreferred
            ? chosenTeamMemberId
            : defaultTeamMemberId;
    const selectedMember =
        roster.find((member) => member.teamMemberId === selectedTeamMemberId) ??
        null;

    const target = resolveMeetingTarget({
        routedSessionId,
        selectedMember,
        assignedSessionId,
    });
    const sessionId = target.mode === 'session' ? target.sessionId : null;

    /**
     * Real availability, fetched only for a session the target lands on. A
     * practice with no connected calendar never calls it.
     */
    const sessionFetcher = useFetcher<BookingAvailabilityApiResponse>();
    const sessionLoadedRef = useRef<number | null>(null);
    useEffect(() => {
        if (preview || !sessionId || sessionLoadedRef.current === sessionId) {
            return;
        }
        sessionLoadedRef.current = sessionId;
        sessionFetcher.load(
            `${BOOKING_AVAILABILITY_API_ROUTE}?sessionId=${sessionId}&timezone=${encodeURIComponent(serverTimezone)}&singleTimezone=1`,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId, serverTimezone, preview]);
    const previewSessionData = useMemo<
        BookingAvailabilityApiResponse | undefined
    >(
        () =>
            preview
                ? {
                      availability: preview.sessionAvailability
                          ? {
                                ...preview.sessionAvailability,
                                sessionID: sessionId ?? 0,
                            }
                          : preview.requestAvailability,
                  }
                : undefined,
        [preview, sessionId],
    );
    const sessionData = previewSessionData ?? sessionFetcher.data;
    // A fetcher keeps its last answer while it loads the next session, which
    // would show one person's calendar under another's name.
    const sessionAvailability =
        sessionData?.availability &&
        (preview || sessionData.availability.sessionID === sessionId)
            ? sessionData.availability
            : undefined;

    const isRequest = target.mode === 'request';
    const loaded = isRequest ? requestAvailability : sessionAvailability;
    /** A request with nowhere to land: the practice takes no enquiries. */
    const requestWithNoEnquiry = isRequest && !allowsEnquiries;

    /**
     * Demand with nowhere to land is still demand. The dead end is otherwise
     * invisible — nothing is written anywhere — so it emits its own event.
     */
    const tracker = useTracker();
    const deadEndTrackedRef = useRef(false);
    useEffect(() => {
        if (preview || !requestWithNoEnquiry || deadEndTrackedRef.current) {
            return;
        }
        deadEndTrackedRef.current = true;
        const payload = {
            authSource: 'bookMeeting',
            professional: intent.professionalUrlSegment,
        };
        void tracker.log('BookMeetingDeadEnd', {
            url: new URL(window.location.href),
            data: {
                ...payload,
                Source: payload.authSource,
                ExtraData: JSON.stringify(payload),
            },
        });
    }, [requestWithNoEnquiry, tracker, intent.professionalUrlSegment, preview]);

    // A roster failure only matters when the time would be requested: a
    // session books on its own availability, which answers for itself.
    const loadError =
        (isRequest ? rosterData?.error : sessionData?.error) ??
        (requestWithNoEnquiry
            ? {
                  code: 'enquiriesClosed' as const,
                  message: ENQUIRIES_CLOSED_MESSAGE,
              }
            : undefined);
    const isLoading = preview
        ? false
        : isRequest
          ? rosterFetcher.state !== 'idle' || (!loaded && !loadError)
          : sessionFetcher.state !== 'idle' || (!loaded && !loadError);

    const availability = useMemo(() => {
        if (!loaded || requestWithNoEnquiry) return undefined;
        return preferredIsUnknown && isRequest
            ? toCompanyTierAvailability(loaded)
            : loaded;
    }, [loaded, preferredIsUnknown, requestWithNoEnquiry, isRequest]);

    const booking = useBookingSchedule({ availability, serverTimezone });

    const details = useMemo(() => readMeetingDetails(loaded), [loaded]);
    useEffect(() => {
        onDetails?.(details);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [details]);

    const professionalName =
        booking.professional?.title ?? intent.professionalName ?? 'the team';

    /**
     * Who the reader is meeting. The roster names a chosen person; a session
     * booked for "anyone" names its one-on-one host, and a round robin stays
     * with the practice because whoever is free takes it.
     */
    const sessionHostName =
        target.mode === 'session' && !selectedMember
            ? booking.consultedMember?.name || null
            : null;
    const meetingSubjectName =
        preferredIsUnknown || !selectedMember
            ? sessionHostName
            : selectedMember.name;
    const isCompany = meetingSubjectName === null;
    const subjectName = meetingSubjectName ?? professionalName;

    /**
     * Round robin decides who hosts, so a reader on "anyone" is not offered a
     * choice it would override. A routed session decides for everyone.
     */
    const anyoneIsRoundRobin =
        selectedTeamMemberId === null &&
        target.mode === 'session' &&
        sessionAvailability?.meetingType === 'random';
    const pickerRoster =
        lockedToPreferred ||
        preferredIsUnknown ||
        routedSessionId ||
        anyoneIsRoundRobin
            ? EMPTY_ROSTER
            : roster;

    useEffect(() => {
        if (!availability) return;
        onSubject?.({
            subjectName,
            professionalName,
            isCompany,
            image:
                selectedMember?.image ??
                booking.consultedMember?.avatarUrl ??
                undefined,
            companyLogo: booking.professional?.companyLogo,
            logoBackgroundColor: booking.professional?.logoBackgroundColor,
            isBooked: !isRequest,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        availability,
        subjectName,
        professionalName,
        selectedMember,
        booking.consultedMember,
        isRequest,
    ]);

    const handleContinue = () => {
        const startTimestamp = booking.selectedSlotTimestamp;
        if (!startTimestamp) return;
        onSlotChosen({
            sessionId,
            source: target.mode === 'session' ? target.source : 'request',
            teamMemberId: preferredIsUnknown ? null : target.teamMemberId,
            organizerMemberId: isRequest
                ? null
                : booking.selectedOrganizerMemberId,
            startTimestamp,
            duration: booking.selectedDuration,
            timezoneName: booking.timezoneName,
            subjectName,
            isCompany,
        });
    };

    // Only a real failure takes the step off screen. While the fetch is in
    // flight the step draws itself, with the month grid as the one part
    // waiting — see BookMeetingGateSteps.
    if (loadError && !availability) {
        return (
            <BookMeetingGateUnavailable
                code={loadError.code as BookMeetingUnavailableCode}
                message={loadError.message}
                professionalName={professionalName}
                onEnquireInstead={onEnquireInstead}
            />
        );
    }

    return (
        <BookMeetingGateSteps
            booking={booking}
            subjectName={subjectName}
            professionalName={professionalName}
            isCompany={isCompany}
            onContinue={handleContinue}
            onBack={onBack}
            isLoading={isLoading}
            isBooked={!isRequest}
            expectsTeamPicker={
                (intent.expectsTeamPicker ?? false) && !lockedToPreferred
            }
            roster={pickerRoster}
            selectedTeamMemberId={selectedTeamMemberId}
            onSelectTeamMember={setSelectedTeamMemberId}
            companyLogo={
                booking.professional?.companyLogo ?? intent.companyLogo
            }
            logoBackgroundColor={intent.logoBackgroundColor}
            details={details}
            notice={notice}
            ctaLabel={intent.ctaLabel}
        />
    );
};
